import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Receipt, Calendar, Loader2, Search, Pencil } from "lucide-react";
import { billingAPI } from "@/lib/api";
import ContractForm from "@/components/ContractForm";
import { InvoicePreviewSendDialog } from "@/components/billing/InvoicePreviewSendDialog";
import { toast } from "sonner";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

export default function BillingForecastPage({ user }) {
  const navigate = useNavigate();
  const hasContrats = (user?.modules || []).includes("contrats");

  useEffect(() => {
    if (!hasContrats) navigate("/", { replace: true });
  }, [hasContrats, navigate]);

  const [allForecast, setAllForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [search, setSearch] = useState("");
  const [editContractId, setEditContractId] = useState(null);
  const [editClientId, setEditClientId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const reload = useCallback(() => {
    billingAPI.forecast().then(r => setAllForecast(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const threeMonthsLater = new Date();
  threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
  const limitDate = threeMonthsLater.toISOString().split("T")[0];
  const forecast = showAll ? allForecast : allForecast.filter(f => f.date <= limitDate);

  const generateInvoice = async (item) => {
    setGenerating(prev => ({ ...prev, [item.contract_id]: true }));
    try {
      const res = await billingAPI.generate({ contract_id: item.contract_id });
      toast.success(`Facture ${res.data.number} créée`);
      setPreviewDoc(res.data);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setGenerating(prev => ({ ...prev, [item.contract_id]: false }));
    }
  };

  const openEdit = (item) => {
    setEditContractId(item.contract_id);
    setEditClientId(item.client_id);
  };

  const today = new Date().toISOString().split("T")[0];
  const filtered = search
    ? forecast.filter(f => f.client_name?.toLowerCase().includes(search.toLowerCase()) || f.product_name?.toLowerCase().includes(search.toLowerCase()) || f.contact_nom?.toLowerCase().includes(search.toLowerCase()))
    : forecast;

  const totalHT = filtered.reduce((s, f) => s + (f.client_price || 0), 0);
  const pastDue = filtered.filter(f => f.date < today);
  const upcoming = filtered.filter(f => f.date >= today);

  if (loading) return <div className="page-container flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;

  return (
    <div className="page-container" data-testid="billing-forecast-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Factures à établir</h1>
        <p className="text-slate-500 mt-1">
          {showAll ? "Tous les contrats actifs" : "Contrats actifs a facturer dans les 3 prochains mois"}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <p className="text-xs text-slate-500 mb-1">Total a facturer</p>
          <p className="text-2xl font-bold text-slate-900 money-value" data-testid="forecast-total">{formatEur(totalHT)}</p>
          <p className="text-xs text-slate-400">{filtered.length} contrat{filtered.length > 1 ? "s" : ""} HT</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-red-500 mb-1">En retard</p>
          <p className="text-2xl font-bold text-red-600 money-value" data-testid="forecast-overdue">{formatEur(pastDue.reduce((s, f) => s + (f.client_price || 0), 0))}</p>
          <p className="text-xs text-slate-400">{pastDue.length} contrat{pastDue.length > 1 ? "s" : ""}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-emerald-500 mb-1">A venir</p>
          <p className="text-2xl font-bold text-emerald-600 money-value" data-testid="forecast-upcoming">{formatEur(upcoming.reduce((s, f) => s + (f.client_price || 0), 0))}</p>
          <p className="text-xs text-slate-400">{upcoming.length} contrat{upcoming.length > 1 ? "s" : ""}</p>
        </Card>
      </div>

      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher client ou produit..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="forecast-search" />
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm text-slate-400">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</p>
            {!showAll && allForecast.length > forecast.length && (
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setShowAll(true)} data-testid="show-all-btn">
                Voir tout ({allForecast.length})
              </Button>
            )}
            {showAll && (
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setShowAll(false)} data-testid="show-3months-btn">
                3 prochains mois
              </Button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <Calendar size={48} className="mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
            <p className="text-slate-500">Aucune facture a etablir.</p>
          </div>
        ) : (
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="uppercase text-xs text-slate-500">Nom Société</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500">Nom</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500">Prénom</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500">Produit</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500">Date Facturation</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">Montant HT</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item, i) => {
                  const isPast = item.date < today;
                  return (
                    <TableRow key={item.id || `forecast-${i}`} className={`table-row-hover ${isPast ? 'bg-red-50/50' : ''}`} data-testid={`forecast-row-${i}`}>
                      <TableCell>
                        <button onClick={() => navigate(`/clients/${item.client_id}`)} className="text-sm text-indigo-600 hover:underline font-medium" data-testid={`forecast-client-${i}`}>
                          {item.client_name}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700 uppercase">{item.contact_nom || "-"}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.contact_prenom || "-"}</TableCell>
                      <TableCell className="text-sm">{item.product_name}{item.label ? ` — ${item.label}` : ""}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{new Date(item.date + "T00:00:00").toLocaleDateString("fr-FR")}</span>
                          {isPast && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Retard</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="money-value">{formatEur(item.client_price)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Modifier le contrat" onClick={() => openEdit(item)} data-testid={`edit-contract-${i}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => generateInvoice(item)}
                            disabled={generating[item.contract_id]}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7"
                            data-testid={`generate-invoice-${i}`}
                          >
                            {generating[item.contract_id] ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Receipt size={14} className="mr-1" />}
                            Facturer
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <ContractForm
        open={!!editContractId}
        onOpenChange={(v) => { if (!v) { setEditContractId(null); setEditClientId(null); } }}
        clientId={editClientId}
        contractId={editContractId}
        onSaved={reload}
      />

      <InvoicePreviewSendDialog
        open={!!previewDoc}
        onOpenChange={(v) => { if (!v) setPreviewDoc(null); }}
        doc={previewDoc}
        onSent={reload}
      />
    </div>
  );
}
